import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthShell } from "@/pages/Login";
import { api, apiError, dashboardPath, onboardingPath } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function VerifyPhone() {
  const { user, refresh, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState(location.state?.devOtp || "");
  const [busy, setBusy] = useState(false);

  if (user && user.phone_verified) {
    navigate(user.onboarding_complete ? dashboardPath(user.role) : onboardingPath(user.role), { replace: true });
  }

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/verify-otp", { code });
      const fresh = await refresh();
      toast.success("Phone verified");
      navigate(data.next || dashboardPath(fresh?.role), { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/api/auth/resend-otp");
      setDevOtp(data.dev_otp || "");
      toast.success("New code sent", {
        description: data.dev_otp ? `SMS unavailable — use code ${data.dev_otp}` : "Check your phone for the code",
      });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <AuthShell
      title="Verify your phone number"
      subtitle={`We sent a 6-digit code by SMS to ${user?.phone || "your phone"}.`}
      footer={
        <button onClick={() => { logout(); navigate("/login"); }} className="text-emerald-800" data-testid="verify-signout-link">
          Use a different account
        </button>
      }
    >
      {devOtp && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="dev-otp-banner">
          Demo code: <span className="font-mono font-bold" data-testid="dev-otp-value">{devOtp}</span>
        </div>
      )}
      <form onSubmit={verify} className="space-y-4">
        <Input
          inputMode="numeric"
          maxLength={6}
          required
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          className="text-center font-mono text-2xl tracking-[0.4em]"
          data-testid="otp-input"
        />
        <Button type="submit" disabled={busy || code.length !== 6} className="w-full rounded-full bg-emerald-900 py-6 hover:bg-emerald-800" data-testid="otp-verify-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify phone"}
        </Button>
        <Button type="button" variant="ghost" className="w-full" onClick={resend} data-testid="otp-resend-btn">
          Resend code
        </Button>
      </form>
    </AuthShell>
  );
}
